import { Store } from '@ngrx/store';
import { auditTime, combineLatest, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as CompanyActions from './company/company.actions';
import { selectCompanyState } from './company/company.selector';
import * as EquipmentActions from './equipment/equipment.actions';
import * as ProfileActions from './profile/profile.actions';
import { loadingProfile } from './profile/profile.actions';
import { selectProfileState } from './profile/profile.selector';
import { CompanyState, LoadingState, ProfileState } from './store.states';

let lastLoad = {
  profile: new Date(0),
  company: new Date(0),
  members: new Date(0),
  equipment: new Date(0),
};

const LOADING_LOCK_TIME_IN_MS = 30000;

export const dispatchLoadingProfileWithCompanyAndEquipment = (
  store: Store,
  options: {
    timeLockedProfile?: boolean;
    includeCompany?: boolean;
    includeMembers?: boolean;
    includeEquipment?: boolean;
  } = {},
) => {
  const {
    timeLockedProfile = false,
    includeCompany = true,
    includeMembers = false,
    includeEquipment = false,
  } = options;

  const currentTime = new Date();
  if (
    !timeLockedProfile ||
    currentTime.getTime() - lastLoad.profile.getTime() > LOADING_LOCK_TIME_IN_MS
  ) {
    lastLoad.profile = currentTime;
    store.dispatch(ProfileActions.loadingProfile());
  }
  if (includeCompany) {
    dispatchLoadingCompany(store);
  }
  if (includeMembers) {
    dispatchLoadingMembers(store);
  }
  if (includeEquipment) {
    dispatchLoadingEquipment(store);
  }
};

export const dispatchLoadingProfileTimeLocked = (store: Store) => {
  let currentTime = new Date();
  if (
    currentTime.getTime() - lastLoad.profile.getTime() >
    LOADING_LOCK_TIME_IN_MS
  ) {
    store.dispatch(loadingProfile());
    lastLoad.profile = currentTime;
  }
};

export const dispatchLoadingCompanyTimeLocked = (store: Store) => {
  let currentTime = new Date();
  if (
    currentTime.getTime() - lastLoad.company.getTime() >
    LOADING_LOCK_TIME_IN_MS
  ) {
    dispatchLoadingCompany(store);
    lastLoad.company = currentTime;
  }
};

export const dispatchLoadingMembersTimeLocked = (store: Store) => {
  let currentTime = new Date();
  if (
    currentTime.getTime() - lastLoad.members.getTime() >
    LOADING_LOCK_TIME_IN_MS
  ) {
    dispatchLoadingMembers(store);
    lastLoad.members = currentTime;
  }
};

export const dispatchLoadingCompany = (store: Store) => {
  combineLatest([
    store.select(selectProfileState),
    store.select(selectCompanyState),
  ])
    .pipe(
      filter(
        ([profile]) =>
          profile.loadingState === LoadingState.SUCCESS && !!profile.companyId,
      ),
      auditTime(0),
      take(1),
    )
    .subscribe(([profile, company]: [ProfileState, CompanyState]) => {
      const alreadyLoaded =
        company.id === profile.companyId && company.completed;
      const alreadyLoading =
        company.id === profile.companyId &&
        company.loadingState === LoadingState.PENDING;

      if (alreadyLoaded || alreadyLoading) {
        return;
      }

      store.dispatch(
        CompanyActions.loadingCompany({ companyId: profile.companyId }),
      );
      lastLoad.company = new Date();
    });
};

export const dispatchLoadingMembers = (store: Store) => {
  store
    .select(selectCompanyState)
    .pipe(
      filter(
        (company) =>
          company.loadingState === LoadingState.SUCCESS && !!company.id,
      ),
      take(1),
    )
    .subscribe((company) => {
      store.dispatch(
        CompanyActions.loadingCompanyMembers({
          companyId: company.id!,
        }),
      );
      lastLoad.members = new Date();
    });
};

export const dispatchLoadingEquipment = (store: Store) => {
  store
    .select(selectCompanyState)
    .pipe(
      filter(
        (company) =>
          company.loadingState === LoadingState.SUCCESS && !!company.id,
      ),
      take(1),
    )
    .subscribe((company) => {
      store.dispatch(
        EquipmentActions.loadingEquipment({
          companyId: company.id!,
        }),
      );
      lastLoad.equipment = new Date();
    });
};
